
import { Button } from "@/components/ui/button";
import { SetupGuideDialog } from "./SetupGuideDialog";
import { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCategories } from "@/hooks/useCategories";
import { useContractTypes } from "@/hooks/useContractTypes";
import { useCurrencies } from "@/hooks/useCurrencies";

interface SetupGuideCTAProps {
  type?: "categories" | "contract-types" | "currencies";
  className?: string;
  variant?: "floating" | "card" | "inline";
}

export function SetupGuideCTA({ type = "categories", className, variant = "inline" }: SetupGuideCTAProps) {
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [shouldDisplay, setShouldDisplay] = useState(false);
  const [missingItems, setMissingItems] = useState<string[]>([]);
  const { t } = useTranslation();
  
  // Fetch required data
  const { useCategoriesQuery } = useCategories();
  const { useContractTypesQuery } = useContractTypes();
  const { useCurrenciesQuery } = useCurrencies();
  
  const { data: categories = [] } = useCategoriesQuery();
  const { data: contractTypes = [] } = useContractTypesQuery();
  const { data: currencies = [] } = useCurrenciesQuery();

  useEffect(() => {
    const missing: string[] = [];
    
    if (categories.length === 0) {
      missing.push(t('setupItems.categories', 'categories'));
    }
    
    if (contractTypes.length === 0) {
      missing.push(t('setupItems.contractTypes', 'contract types'));
    }
    
    if (currencies.length === 0) {
      missing.push(t('setupItems.currencies', 'currencies'));
    }
    
    setMissingItems(missing);
    
    // Determine if we should show the CTA based on missing items and selected type
    if (variant === 'floating') {
      // For floating variant, show it if any items are missing
      setShouldDisplay(missing.length > 0);
    } else {
      // For other variants, show it based on the specific type
      if (type === "categories" && categories.length === 0) {
        setShouldDisplay(true);
      } else if (type === "contract-types" && contractTypes.length === 0) {
        setShouldDisplay(true);
      } else if (type === "currencies" && currencies.length === 0) {
        setShouldDisplay(true);
      } else {
        setShouldDisplay(false);
      }
    }
  }, [type, categories.length, contractTypes.length, currencies.length, variant, t]);

  // Don't render anything if shouldDisplay is false
  if (!shouldDisplay) {
    return null;
  }
  
  const getMessage = () => {
    // For floating CTA, create a comprehensive message
    if (variant === 'floating') {
      if (missingItems.length > 0) {
        return t(
          'setupMissingItems', 
          'You have not added {{items}}. Click to start setup guide.',
          { items: missingItems.join(', ') }
        );
      }
    }
    
    // For specific types, use the individual messages
    const messages = {
      "categories": t("noCategoriesMessage", "No categories found. Add some to better organize your contracts."),
      "contract-types": t("noContractTypesMessage", "No contract types found. Add some to better manage your contracts."),
      "currencies": t("noCurrenciesMessage", "No currencies found. Add some to track contract costs.")
    };
    
    return messages[type];
  };

  const getButtonStyle = () => {
    if (variant === "floating") {
      return "fixed bottom-4 right-4 z-50 shadow-lg";
    } else if (variant === "card") {
      return "w-full bg-primary text-white hover:bg-primary/90";
    }
    return `flex items-center gap-2 text-muted-foreground hover:text-primary ${className}`;
  };

  return (
    <>
      {shouldDisplay && (
        <Button
          variant={variant === "card" ? "default" : "outline"}
          size="sm"
          className={getButtonStyle()}
          onClick={() => setShowSetupGuide(true)}
        >
          <BookOpen className="h-4 w-4" />
          <span className="text-sm">{getMessage()}</span>
        </Button>
      )}
      
      <SetupGuideDialog 
        open={showSetupGuide} 
        onOpenChange={setShowSetupGuide}
        initialStep={type}
      />
    </>
  );
}
